import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';

const STORAGE_KEY = 'cookie_consent_v1';

type ConsentValue = 'all' | 'essential' | null;

function getStoredConsent(): ConsentValue {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === 'all' || raw === 'essential') return raw;
  } catch {
    // localStorage blocked (e.g. private browsing on some browsers)
  }
  return null;
}

function storeConsent(value: 'all' | 'essential'): void {
  try {
    localStorage.setItem(STORAGE_KEY, value);
  } catch {
    // ignore
  }
}

export function CookieBanner() {
  const { t } = useTranslation('common');
  const [consent, setConsent] = useState<ConsentValue>(() => getStoredConsent());

  useEffect(() => {
    // Re-read on mount in case another tab set it
    setConsent(getStoredConsent());
  }, []);

  if (consent !== null) return null;

  function accept(value: 'all' | 'essential') {
    storeConsent(value);
    setConsent(value);
  }

  return (
    <div
      role="dialog"
      aria-label="Piškotki"
      className="fixed bottom-0 left-0 right-0 z-50 border-t border-linen bg-white/95 backdrop-blur-sm shadow-elevated"
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <p className="text-sm text-iron flex-1">
          {t('cookie.message')}
        </p>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              // Settings placeholder — can expand later
              accept('essential');
            }}
          >
            {t('cookie.settings')}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => accept('essential')}
          >
            {t('cookie.essential_only')}
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={() => accept('all')}
          >
            {t('cookie.accept_all')}
          </Button>
        </div>
      </div>
    </div>
  );
}
